import { useState, useEffect } from 'react';
import { useApp } from '../App';
import api from '../api';
import BackButton from '../components/BackButton';
import { useI18n } from '../i18n/index.jsx';

export default function AdminReceipts() {
  const [receipts, setReceipts] = useState([]);
  const { t } = useI18n();
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [selected, setSelected] = useState(null);
  const [approveForm, setApproveForm] = useState({ amount: '', currency: 'PLN', comment: '' });
  const [rejectComment, setRejectComment] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadReceipts();
  }, [filter]);

  async function loadReceipts() {
    try {
      const endpoint = filter === 'pending' ? '/receipts/admin/pending' : '/receipts/admin/all';
      const { data } = await api.get(endpoint);
      setReceipts(data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  async function approve() {
    if (!approveForm.amount || parseFloat(approveForm.amount) <= 0) {
      alert('Укажите сумму');
      return;
    }
    setProcessing(true);
    try {
      await api.post(`/receipts/admin/approve/${selected.id}`, {
        amount: parseFloat(approveForm.amount),
        currency: approveForm.currency,
        comment: approveForm.comment || null,
      });
      setSelected(null);
      setApproveForm({ amount: '', currency: 'PLN', comment: '' });
      loadReceipts();
    } catch (e) {
      alert(e.userMessage || 'Ошибка');
    } finally {
      setProcessing(false);
    }
  }

  async function reject() {
    if (!rejectComment.trim()) {
      alert('Укажите причину отклонения');
      return;
    }
    setProcessing(true);
    try {
      await api.post(`/receipts/admin/reject/${selected.id}`, {
        comment: rejectComment.trim(),
      });
      setSelected(null);
      setRejectComment('');
      loadReceipts();
    } catch (e) {
      alert(e.userMessage || 'Ошибка');
    } finally {
      setProcessing(false);
    }
  }

  function formatDate(isoString) {
    if (!isoString) return '';
    const d = new Date(isoString);
    return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  }

  const statusLabels = {
    pending: '⏳ Ожидает',
    approved: '✅ Одобрен',
    rejected: '❌ Отклонён',
  };

  if (loading) return <div className="page"><BackButton /><div className="loader">{t('common.loading')}</div></div>;

  const pendingCount = receipts.filter(r => r.status === 'pending').length;

  return (
    <div className="page">
      <BackButton />
      <h1>🧾 Чеки и фактуры</h1>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button
          className={`btn ${filter === 'pending' ? 'btn-primary' : ''}`}
          style={{ background: filter === 'pending' ? undefined : 'rgba(255,255,255,0.1)' }}
          onClick={() => setFilter('pending')}
        >
          ⏳ Ожидают ({pendingCount})
        </button>
        <button
          className={`btn ${filter === 'all' ? 'btn-primary' : ''}`}
          style={{ background: filter === 'all' ? undefined : 'rgba(255,255,255,0.1)' }}
          onClick={() => setFilter('all')}
        >
          Все
        </button>
      </div>

      {/* Modal */}
      {selected && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.9)', zIndex: 100,
          display: 'flex', flexDirection: 'column', padding: 20,
          overflow: 'auto',
        }}>
          <div style={{ textAlign: 'right', marginBottom: 12 }}>
            <button className="btn" onClick={() => setSelected(null)}>✕ Закрыть</button>
          </div>
          
          {/* Image */}
          <img
            src={selected.image_url}
            alt="Receipt"
            style={{
              maxWidth: '100%',
              maxHeight: '50vh',
              objectFit: 'contain',
              borderRadius: 8,
              marginBottom: 16,
            }}
          />

          <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: 12, padding: 16 }}>
            <div style={{ marginBottom: 12 }}>
              <b>Пользователь:</b> {selected.user_name || `#${selected.user_id}`}
            </div>
            <div style={{ marginBottom: 16 }}>
              <b>Загружен:</b> {formatDate(selected.created_at)}
            </div>

            {selected.status === 'pending' ? (
              <>
                {/* Approve Form */}
                <div style={{ marginBottom: 16 }}>
                  <h4 style={{ marginTop: 0 }}>✅ Одобрить</h4>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    <input
                      type="number"
                      value={approveForm.amount}
                      onChange={(e) => setApproveForm({ ...approveForm, amount: e.target.value })}
                      placeholder="Сумма"
                      style={{
                        flex: 1, padding: 12, borderRadius: 8,
                        border: '1px solid rgba(255,255,255,0.2)',
                        background: 'rgba(255,255,255,0.05)', color: '#fff',
                      }}
                    />
                    <select
                      value={approveForm.currency}
                      onChange={(e) => setApproveForm({ ...approveForm, currency: e.target.value })}
                      style={{
                        padding: 12, borderRadius: 8,
                        border: '1px solid rgba(255,255,255,0.2)',
                        background: 'rgba(255,255,255,0.05)', color: '#fff',
                      }}
                    >
                      <option value="PLN">PLN</option>
                      <option value="EUR">EUR</option>
                      <option value="USD">USD</option>
                      <option value="USDT">USDT</option>
                    </select>
                  </div>
                  <input
                    type="text"
                    value={approveForm.comment}
                    onChange={(e) => setApproveForm({ ...approveForm, comment: e.target.value })}
                    placeholder="Комментарий (опционально)"
                    style={{
                      width: '100%', padding: 12, borderRadius: 8, marginBottom: 8,
                      border: '1px solid rgba(255,255,255,0.2)',
                      background: 'rgba(255,255,255,0.05)', color: '#fff',
                    }}
                  />
                  <button
                    className="btn btn-primary"
                    style={{ width: '100%' }}
                    onClick={approve}
                    disabled={processing}
                  >
                    {processing ? '⏳...' : '✅ Одобрить и зачислить'}
                  </button>
                </div>

                {/* Reject Form */}
                <div>
                  <h4 style={{ marginTop: 0 }}>❌ Отклонить</h4>
                  <input
                    type="text"
                    value={rejectComment}
                    onChange={(e) => setRejectComment(e.target.value)}
                    placeholder="Причина отклонения"
                    style={{
                      width: '100%', padding: 12, borderRadius: 8, marginBottom: 8,
                      border: '1px solid rgba(255,255,255,0.2)',
                      background: 'rgba(255,255,255,0.05)', color: '#fff',
                    }}
                  />
                  <button
                    className="btn"
                    style={{ width: '100%', background: 'rgba(239,68,68,0.3)' }}
                    onClick={reject}
                    disabled={processing}
                  >
                    {processing ? '⏳...' : '❌ Отклонить'}
                  </button>
                </div>
              </>
            ) : (
              <div>
                <div style={{ marginBottom: 8 }}>
                  <b>Статус:</b> {statusLabels[selected.status]}
                </div>
                {selected.amount && (
                  <div style={{ marginBottom: 8 }}>
                    <b>Сумма:</b> {selected.amount} {selected.currency}
                  </div>
                )}
                {selected.admin_comment && (
                  <div>
                    <b>Комментарий:</b> {selected.admin_comment}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* List */}
      {receipts.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: 32,
          background: 'rgba(255,255,255,0.05)',
          borderRadius: 12,
        }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🧾</div>
          <p style={{ opacity: 0.7 }}>Нет чеков</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
          {receipts.map(r => (
            <div
              key={r.id}
              onClick={() => setSelected(r)}
              style={{
                background: 'rgba(255,255,255,0.05)',
                borderRadius: 12,
                overflow: 'hidden',
                cursor: 'pointer',
              }}
            >
              <img
                src={r.image_url}
                alt="Receipt"
                style={{
                  width: '100%',
                  height: 120,
                  objectFit: 'cover',
                }}
              />
              <div style={{ padding: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: 'bold' }}>#{r.id}</span>
                  <span style={{
                    padding: '2px 6px',
                    borderRadius: 4,
                    fontSize: 10,
                    background: r.status === 'pending' ? '#f59e0b' : r.status === 'approved' ? '#10b981' : '#ef4444',
                  }}>
                    {r.status}
                  </span>
                </div>
                <div style={{ fontSize: 11, opacity: 0.6 }}>{formatDate(r.created_at)}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
